import { Send } from 'lucide-react'
import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Label } from '@/components/ui/Label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'

interface PreviewConversation {
  id: string
  label: string
  visitor_email: string
}

interface EmailTemplateTestCardProps {
  conversations: PreviewConversation[]
  currentUserEmail: string
}

const TEMPLATES = [
  { value: 'visitor_follow_up', label: 'Follow-up' },
  { value: 'agent_reply', label: 'Agent reply' },
  { value: 'csat_request', label: 'Feedback request (CSAT)' },
  { value: 'transcript', label: 'Conversation transcript' },
] as const

type TemplateValue = typeof TEMPLATES[number]['value']

export function EmailTemplateTestCard({ conversations, currentUserEmail }: EmailTemplateTestCardProps) {
  const { withAccount } = useRouteParams()
  const [template, setTemplate] = useState<TemplateValue>('visitor_follow_up')
  const [conversationId, setConversationId] = useState<string>(conversations[0]?.id ?? '')
  const [sending, setSending] = useState(false)

  const handleSend = () => {
    if (!conversationId) return
    setSending(true)
    router.post(
      routes.settings.sendTestEmail.path(withAccount()),
      { template, conversation_id: conversationId },
      {
        preserveScroll: true,
        onFinish: () => setSending(false),
      }
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Send className="size-5" />
          Email Templates
        </CardTitle>
        <CardDescription>
          Send a test email to yourself to preview how outbound emails look — including the
          colors and footer from your email branding above.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Template</Label>
          <Select value={template} onValueChange={(v) => setTemplate(v as TemplateValue)}>
            <SelectTrigger className="cursor-pointer">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEMPLATES.map(t => (
                <SelectItem key={t.value} value={t.value} className="cursor-pointer">
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Conversation</Label>
          {conversations.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No conversations with visitor emails found. Start a conversation where the visitor has provided their email.
            </p>
          ) : (
            <Select value={conversationId} onValueChange={setConversationId}>
              <SelectTrigger className="cursor-pointer">
                <SelectValue placeholder="Select a conversation…" />
              </SelectTrigger>
              <SelectContent>
                {conversations.map(c => (
                  <SelectItem key={c.id} value={c.id} className="cursor-pointer">
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-muted-foreground">
            Will be sent to <strong className="text-foreground">{currentUserEmail}</strong>
          </p>
          <Button
            onClick={handleSend}
            disabled={sending || !conversationId}
            className="cursor-pointer"
          >
            <Send className="size-4 mr-2" />
            {sending ? 'Sending…' : 'Send test email'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
